import { useSelector } from 'react-redux';
import { ExternalLinkIcon } from '@rsos/assets/icons';
import { Box, Button, Text } from '@rsos/components/capstone/base';
import {
  AGENT511,
  AGENT_511,
  TEXT_TO_911_MESSAGE,
} from '@rsos/constants/agent511';
import { patchAgent511SessionID } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsAPI';
import checkAgent511Access from '@rsos/utils/checkAgent511Access';
import { getMostRecentLocations } from '@rsos/utils/getMostRecentLoc';
import { getAgent511Width, getAgent511Height } from '@rsos/utils/metaTags';
import openRequestedPopup from '@rsos/utils/openRequestedPopup';
import { captureExceptionWithScope } from '@rsos/utils/sentry';
import useIsIRP from '@rsos/utils/useIsIRP';
import {
  StyledCard,
  StyledCardBody,
} from '../AdditionalData/EllingtonCard/CustomCard.styles';
import CustomCardHeader from '../AdditionalData/EllingtonCard/CustomCardHeader';
import selectADRForCall from '../AdditionalData/EllingtonCard/selectADRForCall';
import { isExtraInfoOrMeta } from '../JurisdictionView/helpers';
import { TextTo911Message, TextTo911MessageText } from './TextTo911Card.styles';
import { trackTextTo911CardClick } from './TextTo911Trackings';

const screenWidth = screen.width;
const screenHeight = screen.height;
const windowFeatures = `width=${getAgent511Width()},height=${getAgent511Height()},left=${
  screenWidth - getAgent511Width()
},top=${screenHeight - getAgent511Height()}`;

const TextTo911Card = () => {
  const { irpVersion } = useIsIRP();
  const psaps = useSelector(state => state.psaps);
  const sinatra = useSelector(state => state.sinatra);
  const hasAgent511Access = checkAgent511Access(psaps, sinatra);
  const callQueue = useSelector(state => state.emergencyCalls.callQueue);
  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );
  const { mostRecentLocation } = getMostRecentLocations(
    callQueue[selectedCallerID],
  );
  const tt911ADR = useSelector(
    state => selectADRForCall(state, selectedCallerID)?.[AGENT_511]?.[0],
  );
  const createdTime = tt911ADR?.createdTime;
  const agent511SessionID =
    tt911ADR?.data?.agent_511_session_id ||
    isExtraInfoOrMeta(mostRecentLocation) ||
    null;
  const message = tt911ADR?.data?.message;
  const url = tt911ADR?.data?.url;
  const orgName = sinatra.user.currentOrg.name;

  const handleReply = async () => {
    trackTextTo911CardClick(irpVersion);
    const data = {
      orgName,
      agent511SessionID,
    };
    try {
      await patchAgent511SessionID(data);
    } catch (error) {
      captureExceptionWithScope(error, {
        extra: {
          errorMessage: error?.message || '',
          agent511SessionID,
        },
      });
    } finally {
      openRequestedPopup(url, '_blank', windowFeatures);
    }
  };

  return hasAgent511Access && tt911ADR ? (
    <StyledCard data-name="text-to-911-card">
      <CustomCardHeader
        data-name="text-to-911-card-header"
        title={TEXT_TO_911_MESSAGE}
        description={`Source: ${AGENT511}`}
        createdTime={createdTime}
      />
      <StyledCardBody data-name="text-to-911-card-body">
        {message && (
          <TextTo911Message>
            <TextTo911MessageText data-name="text-to-911-card-message">
              {message}
            </TextTo911MessageText>
          </TextTo911Message>
        )}
        {url && (
          <Box margin={15} marginTop={0}>
            <Button
              data-name="text-to-911-reply-button"
              grow
              noMargin
              onClick={handleReply}
              type="button"
            >
              <Box horizontal align="center">
                <Text size="small" mr="4px">
                  Reply
                </Text>
                <ExternalLinkIcon width={10} height={10} />
              </Box>
            </Button>
          </Box>
        )}
      </StyledCardBody>
    </StyledCard>
  ) : null;
};

export default TextTo911Card;
